// Utilitário de "compra guiada": todo toque tem resposta.
// Quando a pessoa tenta pagar/continuar com campos faltando, rolamos até o
// primeiro campo inválido, focamos nele (abrindo o teclado), marcamos como
// inválido (borda vermelha + mensagem) e mostramos um toast discreto no rodapé.
// Não muda nenhuma chamada ao banco nem o fluxo de pagamento — só guia a pessoa.

export interface CampoInvalido {
  ref: HTMLElement | null;
  rotulo: string;
  mensagem: string;
}

export interface CampoGuiado {
  ref: HTMLElement | null;
  rotulo: string;
  mensagem: string | null;
}

// Devolve apenas os campos inválidos, na ordem em que aparecem na tela.
export function validar(campos: CampoGuiado[]): CampoInvalido[] {
  return campos
    .filter((c): c is CampoGuiado & { mensagem: string } => Boolean(c.mensagem))
    .map((c) => ({ ref: c.ref, rotulo: c.rotulo, mensagem: c.mensagem as string }));
}

// Localiza o input/select/textarea dentro de um elemento (ou o próprio).
function acharCampo(el: HTMLElement): HTMLElement {
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLSelectElement ||
    el instanceof HTMLTextAreaElement
  ) {
    return el;
  }
  const campo = el.querySelector<HTMLElement>('input, select, textarea');
  return campo ?? el;
}

// Sobe pelos ancestrais até achar a mensagem de erro já renderizada (role=alert).
function acharMensagem(campo: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = campo.parentElement;
  for (let i = 0; i < 6 && el; i += 1) {
    const msg = el.querySelector<HTMLElement>('[data-erro="true"]');
    if (msg) return msg;
    el = el.parentElement;
  }
  return null;
}

// Marca um campo como inválido (aria) e liga à mensagem de erro renderizada.
function marcarInvalido(campo: HTMLElement, mensagem: string): void {
  campo.setAttribute('aria-invalid', 'true');

  const msg = acharMensagem(campo);
  if (msg) {
    if (!msg.id) {
      msg.id = `guiado-msg-${Math.random().toString(36).slice(2, 9)}`;
    }
    campo.setAttribute('aria-describedby', msg.id);
    if (msg.textContent !== mensagem) msg.textContent = mensagem;
  }

  // Limpa sozinho quando a pessoa começa a corrigir o campo.
  const limpar = () => {
    campo.removeAttribute('aria-invalid');
    campo.removeAttribute('aria-describedby');
    campo.removeEventListener('input', limpar);
    campo.removeEventListener('change', limpar);
  };
  campo.addEventListener('input', limpar, { once: true });
  campo.addEventListener('change', limpar, { once: true });
}

let toastTimer: number | undefined;
let toastEl: HTMLDivElement | null = null;

function fecharToast(): void {
  if (toastTimer !== undefined) window.clearTimeout(toastTimer);
  if (toastEl) {
    toastEl.remove();
    toastEl = null;
  }
}

export type TipoToast = 'neutro' | 'sucesso' | 'aviso' | 'erro';

// Toast discreto no rodapé, sem botão: entra deslizando de baixo e some sozinho.
export function mostrarToast(texto: string, duracaoMs = 3000, tipo: TipoToast = 'neutro'): void {
  fecharToast();

  const el = document.createElement('div');
  el.className = `guiado-toast guiado-toast--${tipo}`;
  el.setAttribute('role', 'status');
  el.textContent = texto;
  document.body.appendChild(el);
  toastEl = el;

  requestAnimationFrame(() => el.classList.add('guiado-toast--visivel'));

  toastTimer = window.setTimeout(() => {
    el.classList.remove('guiado-toast--visivel');
    window.setTimeout(() => {
      el.remove();
      if (toastEl === el) toastEl = null;
    }, 200);
  }, duracaoMs);
}

// Rola até um campo e foca nele, com toast — usado para erros que vêm do banco
// (ex.: "Maria já está inscrita neste evento") apontando o campo certo.
export function apontarErro(ref: HTMLElement | null, mensagem: string, duracaoMs = 6000): void {
  if (ref) {
    const campo = acharCampo(ref);
    campo.style.scrollMarginTop = '96px';
    campo.style.scrollMarginBottom = '180px';
    campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => campo.focus({ preventScroll: true }), 300);
  }
  mostrarToast(mensagem, duracaoMs);
}

// Rola até o primeiro campo que falta, foca nele e mostra o toast com a seta.
export function irParaOPrimeiro(invalidos: CampoInvalido[]): void {
  if (invalidos.length === 0) return;

  const primeiro = invalidos[0];

  if (primeiro.ref) {
    const campo = acharCampo(primeiro.ref);
    // Compensa cabeçalho/barra fixa ao rolar — inclusive o rodapé de pagamento,
    // para o campo não ficar escondido atrás dele.
    campo.style.scrollMarginTop = '96px';
    campo.style.scrollMarginBottom = '180px';
    campo.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      marcarInvalido(campo, primeiro.mensagem);
      campo.focus({ preventScroll: true });
    }, 300);
  }

  // Texto simples, sem seta: a pessoa já está sendo levada até o campo.
  const texto =
    invalidos.length === 1
      ? 'Falta preencher 1 campo'
      : `Falta preencher ${invalidos.length} campos`;
  mostrarToast(texto);
}